/**
 * §13 governance: boards, per-item-type status workflows, and the append-only governance_event trail.
 * Defaults are DATA — copied into each tenant's `workflow` rows on first use and editable per tenant/project.
 */
/** Subjects a governance_event can attach to (spec §13). raid_item / change_request are rows in RAID / CR log sheets. */
export const SUBJECT_TYPES = ['raid_item', 'change_request', 'action', 'plan_task', 'document'];
/** What picks the workflow. RAID rows use their Type column (risk, issue, …). */
export const ITEM_TYPES = [
    'risk',
    'issue',
    'assumption',
    'dependency',
    'escalation',
    'decision',
    'change_request',
    'action',
    'plan_task',
    'document',
];
export const BOARD_TYPES = [
    'change_board',
    'design_authority',
    'programme_board',
    'clinical_safety',
    'tech_design',
    'custom',
];
/** `review` (§18): one reviewer's decision on a document version (internal or external). */
export const GOVERNANCE_EVENT_TYPES = [
    'status_change',
    'board_review',
    'assignment',
    'comment',
    'review',
];
export const DECISIONS = [
    'approved',
    'returned',
    'deferred',
    'rejected',
    'noted',
    'changes_requested',
];
/** Semantic meaning of a status, so roll-ups don't depend on configurable labels. */
export const STATUS_CATEGORIES = ['not_started', 'active', 'complete', 'cancelled'];
export const STATUS_COLOURS = ['grey', 'blue', 'amber', 'green', 'red', 'purple'];
const s = (key, label, colour, category, isTerminal = false) => ({
    key,
    label,
    colour,
    category,
    isTerminal,
});
// RAID log entries share a baseline lifecycle; each type still gets its own (independently editable) workflow.
const RAID_BASE = [
    s('open', 'Open', 'blue', 'not_started'),
    s('in_progress', 'In progress', 'amber', 'active'),
    s('monitoring', 'Monitoring', 'purple', 'active'),
    s('at_board', 'At board', 'purple', 'active'),
    s('closed', 'Closed', 'green', 'complete', true),
];
export const DEFAULT_WORKFLOWS = {
    risk: { name: 'Risk', statuses: RAID_BASE },
    issue: { name: 'Issue', statuses: RAID_BASE },
    assumption: { name: 'Assumption', statuses: RAID_BASE },
    dependency: { name: 'Dependency', statuses: RAID_BASE },
    escalation: { name: 'Escalation', statuses: RAID_BASE },
    decision: {
        name: 'Decision',
        statuses: [
            s('proposed', 'Proposed', 'blue', 'not_started'),
            s('at_board', 'At board', 'purple', 'active'),
            s('agreed', 'Agreed', 'green', 'complete', true),
            s('rejected', 'Rejected', 'red', 'cancelled', true),
            s('superseded', 'Superseded', 'grey', 'cancelled', true),
        ],
    },
    // Spec §13 example: Logged → Assessed → At board → Returned for info → Approved/Rejected → Implemented → Closed.
    change_request: {
        name: 'Change request',
        statuses: [
            s('logged', 'Logged', 'blue', 'not_started'),
            s('assessed', 'Assessed', 'amber', 'active'),
            s('at_board', 'At board', 'purple', 'active'),
            s('returned', 'Returned for info', 'amber', 'active'),
            s('approved', 'Approved', 'green', 'active'),
            s('rejected', 'Rejected', 'red', 'cancelled', true),
            s('implemented', 'Implemented', 'green', 'active'),
            s('closed', 'Closed', 'grey', 'complete', true),
        ],
    },
    action: {
        name: 'Action',
        statuses: [
            s('open', 'Open', 'blue', 'not_started'),
            s('in_progress', 'In progress', 'amber', 'active'),
            s('done', 'Done', 'green', 'complete', true),
            s('cancelled', 'Cancelled', 'grey', 'cancelled', true),
        ],
    },
    plan_task: {
        name: 'Plan task',
        statuses: [
            s('not_started', 'Not started', 'grey', 'not_started'),
            s('in_progress', 'In progress', 'amber', 'active'),
            s('at_board', 'At board', 'purple', 'active'),
            s('complete', 'Complete', 'green', 'complete', true),
            s('cancelled', 'Cancelled', 'grey', 'cancelled', true),
        ],
    },
    document: {
        name: 'Document',
        statuses: [
            s('draft', 'Draft', 'grey', 'not_started'),
            s('in_review', 'In review', 'amber', 'active'),
            s('at_board', 'At board', 'purple', 'active'),
            s('returned', 'Returned', 'amber', 'active'),
            s('approved', 'Approved', 'green', 'complete', true),
            s('superseded', 'Superseded', 'grey', 'cancelled', true),
        ],
    },
};
/** Item type of a sheet row: RAID log → its Type column; CR log → change_request; other sheets aren't governed. */
export function rowItemType(gridKind, typeValue) {
    if (gridKind === 'change_log')
        return 'change_request';
    if (gridKind !== 'raid' || typeof typeValue !== 'string')
        return null;
    const t = typeValue.trim().toLowerCase();
    return ITEM_TYPES.includes(t) ? t : null;
}
export function subjectTypeForGrid(gridKind) {
    return gridKind === 'raid' ? 'raid_item' : gridKind === 'change_log' ? 'change_request' : null;
}
/** Stable key for a new status label ("Returned for info" → "returned_for_info"). */
export function statusKey(label) {
    return label
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '')
        .slice(0, 40);
}
/** The status a decision usually leads to, when the item's workflow has it (the minute-taker can change it). */
export const DECISION_SUGGESTS = {
    approved: ['approved', 'closed', 'complete', 'monitoring'],
    returned: ['returned', 'in_progress', 'open'],
    rejected: ['rejected', 'draft', 'cancelled', 'closed'],
};
//# sourceMappingURL=governance.js.map